import {
  ConversationChannelType,
  ConversationParticipantEntity,
  ConversationParticipantOption,
} from '@provision/conversation-types';
import { ConversationChannel, ConversationParticipation } from '@provision/entity-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { validateEmail } from 'src/components';
import {
  appendDeletedClientTextToName,
  Conversation,
  ConversationParticipantItem,
  isClientDeleted,
  PreparedConversationParticipantOption,
} from 'src/graphql';
import {
  ParticipantChannelInfo,
  ParticipantOptionValue,
  ParticipantSelectorItem,
  SelectedParticipants,
} from 'src/pages/Conversations/Chat/ParticipationPanel/types';
import {
  getPreparedParticipantOption,
  isSameParticipant,
} from 'src/pages/Conversations/Chat/utils';
import { useNotifications } from 'src/utils/hooks';

export interface UseSelectedParticipants {
  selectedParticipants: SelectedParticipants;
  toIsModified: boolean;
  addCcParticipant: (value: ParticipantOptionValue) => void;
  setToParticipant: (value: ParticipantOptionValue) => void;
  removeParticipant: (data: ParticipantSelectorItem | null) => void;
  removeEmail: (data: ParticipantSelectorItem, email: string) => void;
  addEmail: (data: ParticipantSelectorItem, email: string) => void;
  getToParticipant: () => ParticipantSelectorItem | undefined;
}

interface GetInitialParticipantsInput {
  conversation: Conversation;
  participations: ConversationParticipation[];
  participants: ConversationParticipantItem[];
  userId?: string;
  brandGroupSenderAddresses: Set<string>;
}

interface UseSelectedParticipantsParams {
  conversation: Conversation;
  participations: ConversationParticipation[];
  participants: ConversationParticipantItem[];
  userId?: string;
}

export const useSelectedParticipants = (
  params: UseSelectedParticipantsParams,
): UseSelectedParticipants => {
  const brandGroupSenderAddresses = useMemo(
    () => getBrandGroupSenderAddresses(params.conversation),
    [params.conversation],
  );

  const initialState = useMemo(() => {
    return getInitialParticipants({
      participations: params.participations,
      conversation: params.conversation,
      participants: params.participants,
      userId: params.userId,
      brandGroupSenderAddresses,
    });
  }, [
    params.conversation,
    params.participants,
    params.userId,
    params.participations,
    brandGroupSenderAddresses,
  ]);

  const [selectedParticipants, setSelectedParticipants] =
    useState<SelectedParticipants>(initialState);

  const [toIsModified, setToIsModified] = useState(false);

  const modifySelectedParticipants = useCallback(
    (getChanges: (prevState: SelectedParticipants) => Partial<SelectedParticipants>) => {
      setSelectedParticipants((currentValue) => ({
        ...currentValue,
        ...getChanges(currentValue),
        modified: true,
      }));
    },
    [setSelectedParticipants],
  );

  const { showErrorAlert } = useNotifications();

  const validateOption = useCallback(
    (
      option: ParticipantOptionValue,
    ): option is PreparedConversationParticipantOption | string => {
      if (!option) return false;
      if (typeof option === 'string' && !validateEmail(option)) {
        showErrorAlert('Email is not valid');
        return false;
      }
      return true;
    },
    [showErrorAlert],
  );

  const addCcParticipant = useCallback(
    (value: ParticipantOptionValue) => {
      if (!validateOption(value)) return;

      const email = (typeof value === 'string' ? value : value?.email)?.toLowerCase();
      if (email && brandGroupSenderAddresses.has(email)) {
        showErrorAlert("This address can't be added to CC");
        return;
      }

      modifySelectedParticipants((prev) => {
        const participantItem = getParticipantItemForOption(value);
        return { items: addParticipantItem(prev.items, participantItem) };
      });
    },
    [modifySelectedParticipants, validateOption, brandGroupSenderAddresses, showErrorAlert],
  );

  const setToParticipant = useCallback(
    (value: ParticipantOptionValue) => {
      if (!validateOption(value)) return;

      modifySelectedParticipants((prev) => {
        const participantItem = getParticipantItemForOption(value);

        const preparedItems =
          prev.to && !isSameParticipant(prev.to, participantItem)
            ? prev.items.filter((item) => !isSameParticipant(item, prev.to!))
            : prev.items;

        const result: Partial<SelectedParticipants> = {
          items: addParticipantItem(preparedItems, participantItem),
        };

        result.to = {
          ...getParticipantIdInfo(participantItem),
          name: typeof value === 'object' ? value.label || value.email : value,
        };
        setToIsModified(true);
        return result;
      });
    },
    [modifySelectedParticipants, validateOption],
  );

  const removeParticipant = useCallback(
    (data: ParticipantSelectorItem | null) => {
      if (!data) return;

      modifySelectedParticipants((prev) => {
        const result: Partial<SelectedParticipants> = {
          items: prev.items.filter((item) => !isSameParticipant(item, data)),
        };
        if (prev.to && isSameParticipant(prev.to, data)) result.to = undefined;
        return result;
      });
    },
    [modifySelectedParticipants],
  );

  const removeEmail = useCallback(
    (data: ParticipantSelectorItem, email: string) => {
      modifySelectedParticipants((currentValue) => ({
        items: currentValue.items.map((item) => {
          if (!isSameParticipant(item, data)) return item;
          return { ...item, channels: getFilteredChannels(item.channels, email) };
        }),
      }));
    },
    [modifySelectedParticipants],
  );

  const addEmail = useCallback(
    (data: ParticipantSelectorItem, email: string) => {
      modifySelectedParticipants((currentValue) => ({
        items: currentValue.items.map((item) => {
          if (!isSameParticipant(item, data)) return item;

          return {
            ...item,
            channels: addChannel(item.channels, getEmailChannelObject(email)),
          };
        }),
      }));
    },
    [modifySelectedParticipants],
  );

  const getToParticipant = useCallback(() => {
    return selectedParticipants.items.find(
      (item) => selectedParticipants.to && isSameParticipant(item, selectedParticipants.to),
    );
  }, [selectedParticipants.items, selectedParticipants.to]);

  useEffect(() => {
    if (!selectedParticipants.modified && params.userId) {
      setSelectedParticipants(initialState);
    }
  }, [initialState, params.userId, selectedParticipants.modified]);

  return {
    selectedParticipants,
    toIsModified,
    setToParticipant,
    addCcParticipant,
    removeParticipant,
    removeEmail,
    addEmail,
    getToParticipant,
  };
};

function getEmailChannelObject(email: string): ParticipantChannelInfo {
  return {
    externalIdentifier: email,
    conversationChannelTypeId: ConversationChannelType.Email,
  };
}

function getFilteredChannels(channels: ParticipantChannelInfo[], emailToExclude: string) {
  return channels.filter((channel) => channel.externalIdentifier !== emailToExclude);
}

function getParticipantItem(
  data: PreparedConversationParticipantOption,
): ParticipantSelectorItem {
  return {
    participantIdentity: data.id,
    fullName: data.label,
    participantEntity: data.participantEntity,
    channels: data.email ? [getEmailChannelObject(data.email)] : [],
    entityPartnerId: data.entityPartnerId,
  };
}

function getUnknownParticipantItem(email: string): ParticipantSelectorItem {
  return {
    participantIdentity: email,
    fullName: email,
    participantEntity: ConversationParticipantEntity.Unknown,
    channels: [getEmailChannelObject(email)],
  };
}

function getParticipantItemForOption(
  option: PreparedConversationParticipantOption | string,
): ParticipantSelectorItem {
  return typeof option === 'string'
    ? getUnknownParticipantItem(option)
    : getParticipantItem(option);
}

function addChannel(
  channels: ParticipantChannelInfo[],
  channelToAdd: ParticipantChannelInfo,
): ParticipantChannelInfo[] {
  if (channels.some((channel) => isSameChannel(channel, channelToAdd))) {
    return channels;
  }
  return [...channels, channelToAdd];
}

function addParticipantItem(
  participants: ParticipantSelectorItem[],
  participantToAdd: ParticipantSelectorItem,
): ParticipantSelectorItem[] {
  const existingParticipant = participants.find((participant) =>
    isSameParticipant(participant, participantToAdd),
  );

  if (!existingParticipant) return [...participants, participantToAdd];

  participantToAdd.channels.forEach((channel) => {
    existingParticipant.channels = addChannel(existingParticipant.channels, channel);
  });

  return participants;
}

function getBrandGroupSenderAddresses(conversation: Conversation): Set<string> {
  const brandGroups = conversation.partner?.brandGroups ?? [];
  const addresses = brandGroups.flatMap((brandGroup) => [
    brandGroup.supportEmail,
    brandGroup.membershipEmail,
    brandGroup.internalEmail,
  ]);
  return new Set(addresses.filter(Boolean).map((address) => address.toLowerCase()));
}

function getInitialParticipants(input: GetInitialParticipantsInput): SelectedParticipants {
  const result: SelectedParticipants = { items: [] };

  const participations = input.participations ?? [];

  if (!participations?.length || !input.userId) return result;

  participations
    .filter((p) => p.isActive)
    .forEach((participation) => {
      // At this point, we display only email channels.
      // Therefore, we need to filter all other types of channels before proceeded.
      if (!isEmailChannel(participation.channel)) return;

      if (
        input.brandGroupSenderAddresses.has(
          participation.channel.externalIdentifier.toLowerCase(),
        )
      ) {
        return;
      }

      const participant = findParticipantByParticipation(input.participants, participation);
      if (!participant || isRsm(participant)) return;

      result.items = addParticipantItem(
        result.items,
        getNewParticipantItemForParticipation(participation, participant),
      );
    });

  if (canBeRecipient(input.conversation.requesterData)) {
    const option = getPreparedParticipantOption(input.conversation.requesterData!);
    const requesterItem = getParticipantItemForOption(option);

    result.items = addParticipantItem(result.items, requesterItem);
    result.to = getParticipantIdInfo(requesterItem);
  }

  // If conversation doesn't have a requester, we still want to pre-populate "TO" field.
  if (!result.to && result.items.length) result.to = getParticipantIdInfo(result.items[0]);

  return result;
}

function isEmailChannel(channel: ConversationChannel): boolean {
  return (
    <ConversationChannelType>channel.conversationChannelTypeId ===
    ConversationChannelType.Email
  );
}

function canBeRecipient(requesterData?: ConversationParticipantOption): boolean {
  switch (requesterData?.participantEntity) {
    case ConversationParticipantEntity.Client:
    case ConversationParticipantEntity.PlatformUser:
      return true;
    case ConversationParticipantEntity.Unknown:
      return Boolean(requesterData.email);
    default:
      return false;
  }
}

function isRsm(participant: {
  participantEntity: ConversationParticipantEntity | string;
}): boolean {
  return (
    <ConversationParticipantEntity>participant.participantEntity ===
    ConversationParticipantEntity.Rsm
  );
}

function findParticipantByParticipation(
  participants: ConversationParticipantItem[],
  participation: ConversationParticipation,
): ConversationParticipantItem | undefined {
  return participants.find(
    (participant) =>
      participant.conversationParticipantId ===
      participation.channel.conversationParticipantId,
  );
}

function getNewParticipantItemForParticipation(
  participation: ConversationParticipation,
  participant: ConversationParticipantItem,
): ParticipantSelectorItem {
  return {
    participantEntity: participant.participantEntity as ConversationParticipantEntity,
    participantIdentity: participant.participantIdentity,
    fullName: getParticipantFullName(participant),
    channels: [getChannelInfo(participation.channel)],
    entityPartnerId: getParticipantEntityPartnerId(participant),
  };
}

function getParticipantEntityPartnerId(
  participant: ConversationParticipantItem,
): number | undefined {
  switch (participant.participantEntity as ConversationParticipantEntity) {
    case ConversationParticipantEntity.Client:
      return participant.client?.partnerId;
    case ConversationParticipantEntity.PlatformUser:
      return participant.platformUser?.partnerId;
    case ConversationParticipantEntity.Rsm:
      return participant.rsmEmail?.partnerId;
    default:
      return undefined;
  }
}

function getParticipantIdInfo(
  participantItem: ParticipantSelectorItem | ConversationParticipantItem,
): Pick<ParticipantSelectorItem, 'participantIdentity' | 'participantEntity'> {
  return {
    participantIdentity: participantItem.participantIdentity,
    participantEntity: participantItem.participantEntity as ConversationParticipantEntity,
  };
}

function getChannelInfo(channel: ConversationChannel): ParticipantChannelInfo {
  return {
    externalIdentifier: channel.externalIdentifier,
    conversationChannelTypeId: channel.conversationChannelTypeId,
  };
}

function getParticipantFullName(participant: ConversationParticipantItem): string {
  if (participant.client) {
    const fullName = [participant.client.firstName, participant.client.lastName].join(' ');
    return isClientDeleted(participant.client)
      ? appendDeletedClientTextToName(fullName)
      : fullName;
  }
  if (participant.platformUser) {
    return [participant.platformUser.firstName, participant.platformUser.lastName].join(' ');
  }
  return participant.participantIdentity;
}

function isSameChannel(first: ParticipantChannelInfo, second: ParticipantChannelInfo) {
  return (
    first.conversationChannelTypeId === second.conversationChannelTypeId &&
    first.externalIdentifier === second.externalIdentifier
  );
}
